import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { sdsColors, sdsRadius } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import PbdqImageLightboxWrapper from './components/PbdqImageLightboxWrapper.comp';
import PbdqVideoLightboxWrapper from './components/PbdqVideoLightboxWrapper.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';

import heroImg from '../../assets/images/PoweredByDQ/SchoolCosHero.png';
import step1MeasureImg from '../../assets/images/PoweredByDQ/SchoolCosHowItWorks1.png';
import step2Video from '../../assets/videos/PoweredByDQ/SchoolCosLearnCreatorDemo.mp4';
import step3TrackImg from '../../assets/images/PoweredByDQ/SchoolCosHowItWorks3.png';
import dashboardOverviewImg from '../../assets/images/PoweredByDQ/SchoolCosContImprove.png';

import receive1LearningSpace from '../../assets/images/PoweredByDQ/SchoolCosSchoolReceive1.png';
import receive2Scorecard from '../../assets/images/PoweredByDQ/SchoolCosSchoolReceive2.png';
import receive3Dashboards from '../../assets/images/PoweredByDQ/SchoolCosSchoolReceive3.png';
import receive4Workspaces from '../../assets/images/PoweredByDQ/SchoolCosSchoolReceive4.png';
import receive5LearnCreator from '../../assets/images/PoweredByDQ/SchoolCosSchoolReceive5.png';
import receive6Monitoring from '../../assets/images/PoweredByDQ/SchoolCosSchoolReceive6.png';

import deliveryTeacherImg from '../../assets/images/PoweredByDQ/SchoolCosDeliveryTeacher.webp';
import deliveryCentralImg from '../../assets/images/PoweredByDQ/SchoolCosDeliveryCentral.webp';

/*
 * School COS Readiness — rebuilt to match the design, styling and content
 * of the mockup HTML ("Powered by DQ - School COS Readiness.html") and the
 * approved visual system.
 */

const MOBILE = '@media (max-width: 820px)';
const NARROW = '@media (max-width: 1180px)';

const useStyles = makeStyles(() => ({
    accent: { color: sdsColors.accent },
    gutter: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: '0 56px',
        [NARROW]: { padding: '0 32px' },
        [MOBILE]: { padding: '0 20px' },
    },

    // ── SECTION 0: HERO ────────────────────────────────────────────────────────
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
        overflow: 'hidden',
    },
    heroContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '28px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    heroTextCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        color: '#1B2430',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
    },
    heroSubLead: {
        fontSize: 15,
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
    },
    heroBtn: {
        justifySelf: 'start',
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1), transform 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
        },
    },
    heroArtCol: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [MOBILE]: {
            maxWidth: 'none',
        },
    },
    heroPhoto: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    // ── SECTION 1: HOW IT WORKS (STEPPER) ─────────────────────────────────────
    stepperSection: {
        background: '#FFFFFF',
    },
    stepperContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    sectionEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    stepperHeaderWrap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px 48px',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    stepperTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '34ch',
        color: '#1B2430',
    },
    stepperIntro: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [MOBILE]: {
            whiteSpace: 'normal',
        },
    },
    stepsNavRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        marginTop: 44,
        padding: '0 clamp(0px, 4vw, 80px)',
        [MOBILE]: {
            padding: 0,
        },
    },
    stepNavBtn: {
        position: 'relative',
        display: 'grid',
        justifyItems: 'center',
        gap: 8,
        padding: '0 8px 14px',
        border: 0,
        outline: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: '#1B2430',
        minWidth: 0,
        WebkitTapHighlightColor: 'transparent',
        '&:focus': {
            outline: 'none',
        },
        '&:focus-visible': {
            outline: 'none',
        },
    },
    stepNavConnectingLine: {
        position: 'absolute',
        top: 34,
        left: '50%',
        width: '100%',
        height: 2,
        background: '#E1E4E9',
        zIndex: 0,
        [MOBILE]: {
            top: 26,
        },
    },
    stepNavCircle: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 70,
        height: 70,
        borderRadius: '50%',
        fontSize: 20,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        zIndex: 1,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1), transform 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            transform: 'scale(1.05)',
        },
        [MOBILE]: {
            width: 50,
            height: 50,
            fontSize: 16,
        },
    },
    stepNavVerb: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
    },
    stepNavHeading: {
        fontSize: 16,
        lineHeight: 1.3,
        fontWeight: 700,
        textAlign: 'center',
        color: '#1B2430',
        [MOBILE]: {
            display: 'none',
        },
    },
    stepActiveBar: {
        position: 'absolute',
        left: '50%',
        bottom: 0,
        width: 36,
        height: 3,
        borderRadius: 2,
        transform: 'translateX(-50%)',
    },
    stepPanel: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr)',
        marginTop: 28,
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        background: '#FFFFFF',
        boxShadow: '0 20px 50px rgba(23, 41, 57, 0.08)',
        overflow: 'hidden',
        [MOBILE]: {
            gridTemplateColumns: '1fr',
        },
    },
    stepTextCol: {
        padding: 'clamp(24px, 3vw, 40px)',
        display: 'grid',
        gap: 16,
        alignContent: 'start',
        minWidth: 0,
    },
    stepPill: {
        display: 'inline-flex',
        justifySelf: 'start',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    stepPanelTitle: {
        fontSize: 'clamp(24px, 2.6vw, 34px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    stepPanelPara: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    stepListBox: {
        padding: '18px 20px',
        borderRadius: 14,
        background: '#FFF8F4',
        display: 'grid',
        gap: 10,
    },
    stepListLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#A63C0C',
    },
    stepList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 8,
    },
    stepListItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 10,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
    },
    stepNavButtonsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: 6,
    },
    stepBtnPrev: {
        height: 44,
        padding: '0 18px',
        borderRadius: 10,
        border: '1px solid #D5D9E0',
        background: '#FFFFFF',
        color: '#1B2430',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'opacity 200ms ease',
        '&:disabled': {
            cursor: 'not-allowed',
        },
    },
    stepBtnNext: {
        height: 44,
        padding: '0 18px',
        borderRadius: 10,
        border: 0,
        background: '#C45A2B',
        color: '#FFFFFF',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 200ms ease, opacity 200ms ease',
        '&:hover:not(:disabled)': {
            background: '#A63C0C',
        },
        '&:disabled': {
            cursor: 'not-allowed',
        },
    },
    stepVisualCol: {
        padding: 'clamp(24px, 3vw, 40px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 0,
    },
    stepCardImage: {
        width: '100%',
        borderRadius: 14,
        border: '1px solid #E6E9EE',
        display: 'block',
        boxShadow: '0 20px 50px rgba(23, 41, 57, 0.12)',
    },

    // Laptop Mockup Frame (used for step video/track & cycle dashboard)
    laptopWrap: {
        width: '100%',
        display: 'grid',
        filter: 'drop-shadow(0 22px 40px rgba(23, 41, 57, 0.2))',
    },
    laptopScreen: {
        background: '#D5D9E0',
        border: '1px solid #C3C9D2',
        borderRadius: '16px 16px 6px 6px',
        padding: '10px 10px 12px',
        position: 'relative',
    },
    laptopScreenCamera: {
        position: 'absolute',
        top: 3,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: '#8A93A0',
        boxShadow: 'inset 0 0 0 1.5px #B8C0CC',
    },
    laptopVideo: {
        width: '100%',
        aspectRatio: '16 / 10',
        objectFit: 'cover',
        display: 'block',
        borderRadius: 8,
        background: '#FFFFFF',
    },
    laptopImg: {
        width: '100%',
        aspectRatio: '16 / 10',
        objectFit: 'cover',
        objectPosition: 'top',
        display: 'block',
        borderRadius: 8,
        background: '#FFFFFF',
    },
    laptopBase: {
        height: 12,
        margin: '0 -4%',
        background: 'linear-gradient(180deg, #D5D9E0 0%, #B8C0CC 100%)',
        borderRadius: '0 0 12px 12px',
        position: 'relative',
    },
    laptopBaseNotch: {
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '16%',
        height: 4,
        borderRadius: '0 0 5px 5px',
        background: '#9AA3B0',
    },

    // ── SECTION 2: CONTINUOUS IMPROVEMENT CYCLE ───────────────────────────────
    cycleSection: {
        background: '#F4F6F9',
    },
    cycleContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    cycleCard: {
        padding: 'clamp(24px, 3vw, 44px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
    },
    cyclePill: {
        display: 'inline-flex',
        width: 'max-content',
        maxWidth: '100%',
        alignItems: 'center',
        padding: '8px 14px',
        borderRadius: 999,
        background: 'rgba(255, 255, 255, 0.12)',
        fontSize: 12,
        lineHeight: 1.3,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#FFFFFF',
        marginBottom: 16,
    },
    cycleTitle: {
        fontSize: 'clamp(26px, 3vw, 40px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        whiteSpace: 'nowrap',
        [MOBILE]: {
            whiteSpace: 'normal',
        },
    },
    cyclePara: {
        fontSize: 15,
        lineHeight: 1.6,
        margin: 0,
        color: '#B7C8DA',
        maxWidth: '100ch',
    },
    cycleStepsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'center',
        marginTop: 32,
    },
    cycleChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px 10px 10px',
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    cycleChipNum: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.18)',
        fontSize: 12,
        fontVariantNumeric: 'tabular-nums',
    },
    cycleArrow: {
        color: '#5D7A96',
    },
    cycleRepeatsText: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#8AA3BC',
        marginLeft: 6,
    },
    cycleDashLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: '28px 0 0',
        color: '#D9E3EE',
        maxWidth: '100ch',
    },
    cycleDashWrap: {
        marginTop: 20,
    },
    cycleDashFlex: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(24px, 3vw, 48px)',
        alignItems: 'center',
    },
    cycleDashTextCol: {
        flex: '1 1 380px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
    },
    cycleDashList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 14,
    },
    cycleDashItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 12,
        fontSize: 15,
        lineHeight: 1.55,
    },
    cycleLevelsText: {
        fontSize: 14,
        lineHeight: 1.5,
        margin: '6px 0 0',
        color: '#B7C8DA',
    },
    cycleDashVisualCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 14,
    },
    laptopScreenBig: {
        background: '#D5D9E0',
        border: '1px solid #C3C9D2',
        borderRadius: '18px 18px 6px 6px',
        padding: '12px 12px 14px',
        position: 'relative',
    },
    laptopScreenCameraBig: {
        position: 'absolute',
        top: 4,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: '#8A93A0',
        boxShadow: 'inset 0 0 0 1.5px #B8C0CC',
    },
    laptopBaseBig: {
        height: 14,
        margin: '0 -4%',
        background: 'linear-gradient(180deg, #D5D9E0 0%, #B8C0CC 100%)',
        borderRadius: '0 0 14px 14px',
        position: 'relative',
    },
    laptopBaseNotchBig: {
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '16%',
        height: 5,
        borderRadius: '0 0 6px 6px',
        background: '#9AA3B0',
    },
    dashboardImg: {
        width: '100%',
        display: 'block',
        borderRadius: 8,
        background: '#FFFFFF',
    },

    // ── SECTION 3: WHAT SCHOOLS RECEIVE ───────────────────────────────────────
    receiveSection: {
        background: '#FFFFFF',
    },
    receiveContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    sectionTitleNowrap: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        whiteSpace: 'nowrap',
        color: '#1B2430',
        [MOBILE]: {
            whiteSpace: 'normal',
        },
    },
    receiveGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 'clamp(12px, 1.4vw, 16px)',
        marginTop: 36,
        [NARROW]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [MOBILE]: {
            gridTemplateColumns: '1fr',
        },
    },
    receiveCard: {
        margin: 0,
        display: 'grid',
        gap: 12,
        padding: '16px 10px 10px',
        border: '1px solid #E6E9EE',
        borderRadius: 18,
        background: '#FFFFFF',
    },
    receiveCaption: {
        padding: '0 6px',
        fontSize: 15,
        lineHeight: 1.4,
        fontWeight: 700,
        color: '#1B2430',
    },
    receiveImg: {
        width: '100%',
        aspectRatio: '4 / 3',
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 10,
        display: 'block',
        background: '#F4F6F9',
    },
    receiveFootnote: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: '20px 0 0',
        color: '#5B6470',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [MOBILE]: {
            whiteSpace: 'normal',
        },
    },

    // ── SECTION 4: TWO WAYS TO CREATE THE LEARNING ─────────────────────────────
    waysSection: {
        background: '#FFF8F4',
    },
    waysContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    waysSubtitle: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        maxWidth: '72ch',
        color: '#5B6470',
    },
    waysGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 40,
    },
    wayCardTeacher: {
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ED6628',
        borderRadius: 24,
        background: '#FFF8F4',
        overflow: 'hidden',
    },
    wayCardProvider: {
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        background: '#FFFFFF',
        overflow: 'hidden',
    },
    wayImgWrap: {
        padding: '12px 12px 0',
    },
    wayImg: {
        width: '100%',
        aspectRatio: '16 / 9',
        objectFit: 'cover',
        objectPosition: 'top',
        borderRadius: 14,
        border: '1px solid #E6E9EE',
        display: 'block',
    },
    wayBody: {
        padding: '24px clamp(20px, 2.6vw, 32px) clamp(24px, 2.6vw, 32px)',
        display: 'grid',
        gap: 14,
        alignContent: 'start',
        flex: 1,
    },
    wayPillTeacher: {
        display: 'inline-flex',
        justifySelf: 'start',
        alignSelf: 'flex-start',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    wayPillProvider: {
        display: 'inline-flex',
        justifySelf: 'start',
        alignSelf: 'flex-start',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        background: '#EEF3F8',
        color: '#2A4057',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    wayOverline: {
        fontSize: 14,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        fontWeight: 700,
        margin: 0,
        color: '#6B7280',
    },
    wayHeading: {
        fontSize: 'clamp(22px, 2.2vw, 28px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    wayDesc: {
        fontSize: 15,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    wayList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 7,
    },
    wayItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 10,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
    },
    wayBestForTeacher: {
        fontSize: 14.5,
        lineHeight: 1.55,
        margin: 'auto 0 0',
        paddingTop: 14,
        borderTop: '1px solid #F0D9CC',
        color: '#1B2430',
    },
    wayBestForProvider: {
        fontSize: 14.5,
        lineHeight: 1.55,
        margin: 'auto 0 0',
        paddingTop: 14,
        borderTop: '1px solid #E6E9EE',
        color: '#1B2430',
    },

    // ── SECTION 5: WHY SCHOOLS CHOOSE IT ──────────────────────────────────────
    whySection: {
        background: '#F4F6F9',
    },
    whyContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    whyGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
    },
    whyCard: {
        padding: 'clamp(22px, 2.6vw, 30px)',
        borderRadius: 20,
        display: 'grid',
        gap: 12,
        alignContent: 'start',
    },
    whyHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    whyIconBox: {
        display: 'inline-flex',
        flex: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: 12,
        background: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(23, 41, 57, 0.06)',
    },
    whyTitle: {
        fontSize: 18,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        fontWeight: 700,
        margin: 0,
        color: '#1B2430',
    },
    whyDesc: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#3F4752',
    },

    // ── SECTION 6: OPTIONAL EXTENSIONS ────────────────────────────────────────
    extSection: {
        background: '#FFFFFF',
    },
    extContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    extGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 'clamp(12px, 1.4vw, 16px)',
        marginTop: 36,
        [NARROW]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [MOBILE]: {
            gridTemplateColumns: '1fr',
        },
    },
    extCard: {
        display: 'grid',
        gap: 10,
        alignContent: 'start',
        padding: '18px 18px 22px',
        border: '1px solid #E6E9EE',
        borderRadius: 16,
        background: '#FFFFFF',
    },
    extPlus: {
        fontSize: 16,
        fontWeight: 800,
        lineHeight: 1,
    },
    extTitle: {
        fontSize: 16,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    extDesc: {
        fontSize: 13.5,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
    },

    // ── SECTION 7: STANDARDS & FRAMEWORK ALIGNMENT ───────────────────────────
    standardsSection: {
        background: '#FFF8F4',
    },
    standardsContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(28px, 4vw, 64px)',
        alignItems: 'center',
    },
    standardsTextCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
    },
    standardsHeading: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    standardsPara: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    standardsCard: {
        flex: '0 1 340px',
        minWidth: 0,
        padding: 'clamp(22px, 2.6vw, 30px)',
        borderRadius: 20,
        background: '#172939',
        color: '#FFFFFF',
        display: 'grid',
        gap: 10,
    },
    standardsCardLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#8AA3BC',
    },
    standardsCardBig: {
        fontSize: 'clamp(22px, 2vw, 26px)',
        fontWeight: 800,
        letterSpacing: '-0.02em',
    },
    standardsCardDesc: {
        fontSize: 13.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
    },

    // ── SECTION 8: NEXT STEP / BOTTOM CTA ─────────────────────────────────────
    ctaSection: {
        background: '#172939',
        color: '#FFFFFF',
    },
    ctaContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    ctaTextCol: {
        flex: '1 1 420px',
        minWidth: 0,
        display: 'grid',
        gap: 12,
    },
    ctaTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '22ch',
    },
    ctaPara: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
        maxWidth: '60ch',
    },
    ctaBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 54,
        padding: '0 26px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 16,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms ease',
        '&:hover': {
            background: '#D5581D',
        },
    },
}));

// Step definitions
const STEP_COLORS = ['#68C3EE', '#BCD253', '#EDB459'];
const STEP_TINTS = ['#E1F0F9', '#EDF2D5', '#FAEDDA'];
const STEP_INKS = ['#1F5F86', '#4E6A12', '#8A5A10'];

const STEPS_DATA = [
    {
        eyebrow: '01 · Measure',
        h: 'Establish a trusted baseline.',
        b: 'The COS Children Assessment measures students’ digital-citizenship readiness, cyber-risk exposure and priority support needs.',
        b2: 'Individual scorecards and class, school and network dashboards show where students are doing well, where vulnerabilities exist and where intervention should begin.',
        listLabel: 'What you receive:',
        receive: [
            'Individual student scorecards',
            'Class, grade, school and network dashboards',
            'Readiness and cyber-risk profiles',
            'Priority areas for targeted support',
        ],
        out: 'A clear baseline showing what students need and where action should focus.',
        ph: 'Student assessment and scorecard',
    },
    {
        eyebrow: '02 · Build',
        h: 'Turn the findings into targeted learning.',
        b: 'LearnCreator transforms the identified needs into age-appropriate, standards-aligned learning programs.',
        b2: '',
        listLabel: 'What you can do:',
        receive: [
            'Create learning around the gaps identified',
            'Align programs with your curriculum and policies',
            'Adapt content by age, grade, subject or school',
            'Deliver programs centrally or empower teachers to create',
        ],
        out: 'Ready-to-deliver learning designed around students’ actual needs.',
        ph: 'LearnCreator program builder',
    },
    {
        eyebrow: '03 · Track',
        h: 'Show what changed.',
        b: 'Embedded learning impact assessments measure progress against the original baseline.',
        b2: 'Dashboards provide evidence of participation, capability development and areas that still require attention—helping educators improve the next cycle of learning.',
        listLabel: 'What you can track:',
        receive: [
            'Progress by student, class and year group',
            'Improvement by capability or risk area',
            'Program participation and completion',
            'Results across schools and networks',
        ],
        out: 'Evidence of improvement for educators, families, boards and education authorities.',
        ph: 'Progress and impact dashboard',
    },
];

const CYCLE_STEPS = [
    { n: '1', h: 'Measure', isAccent: true },
    { n: '2', h: 'Prioritize', isAccent: false },
    { n: '3', h: 'Create & Deliver', isAccent: false },
    { n: '4', h: 'Improve & Track', isAccent: false },
    { n: '5', h: 'Re-Measure', isAccent: true },
];

const DASH_POINTS = [
    { h: 'Identify where to focus.', b: 'Priority risks, competency gaps, and who needs support.' },
    { h: 'Monitor learning progress.', b: 'Participation, completion, and progress by program.' },
    { h: 'Guide ongoing improvement.', b: 'Reassess, refine programs, and spot where more support is needed.' },
];

const RECEIVE_LIST = [
    { t: 'Student assessment and learning spaces', img: receive1LearningSpace },
    { t: 'Individual student scorecards', img: receive2Scorecard },
    { t: 'Class, school and network dashboards', img: receive3Dashboards },
    { t: 'Teacher and administrator workspaces', img: receive4Workspaces },
    { t: 'LearnCreator access for the selected delivery model', img: receive5LearnCreator },
    { t: 'Tools for monitoring participation and improvement', img: receive6Monitoring },
];

const WHY_ITEMS = [
    {
        h: 'Learning begins with evidence',
        b: 'Schools first establish what students know, experience and need—so teaching is based on evidence rather than assumptions.',
        tint: '#E1F0F9',
        line: '#C9E2F2',
        ink: '#1F5F86',
        icon: 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
    },
    {
        h: 'Support is targeted',
        b: 'Assessment findings guide the learning, helping schools focus time and resources on the most important capability and risk areas.',
        tint: '#EDF2D5',
        line: '#DCE6B8',
        ink: '#4E6A12',
        icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20 M12 6a6 6 0 1 0 0 12 6 6 0 1 0 0-12 M12 10a2 2 0 1 0 0 4 2 2 0 1 0 0-4',
    },
    {
        h: 'It fits existing teaching',
        b: 'Programs can run as dedicated learning or be incorporated into subjects and activities already in the timetable.',
        tint: '#FAEDDA',
        line: '#F3DDB4',
        ink: '#8A5A10',
        icon: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z M9 7h7 M9 11h5',
    },
    {
        h: 'Progress is measurable',
        b: 'Baseline and follow-up measurement show whether students’ capabilities improved—not simply whether they attended.',
        tint: '#F8EAE4',
        line: '#F0D1C3',
        ink: '#A63C0C',
        icon: 'M3 3v18h18 M7 14l4-4 4 4 5-6',
    },
    {
        h: 'It works across multiple levels',
        b: 'Teachers see their classes. School leaders see the whole school. Network administrators see patterns and priorities across participating schools.',
        tint: '#F6E5F0',
        line: '#EBC9DB',
        ink: '#8E1F55',
        icon: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
    },
    {
        h: 'It is nationally and globally aligned',
        b: 'The solution is grounded in the DQ framework underlying IEEE 3527.1™ and informed by more than 100 global and national digital-literacy and child online safety frameworks.',
        tint: '#E8EDF5',
        line: '#CFD9EA',
        ink: '#2A4057',
        icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20 M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10',
    },
];

const EXTENSIONS = [
    {
        h: 'Define your own standard',
        b: 'Use FrameworkCreator to establish grade-by-grade expectations aligned with your curriculum, policies and selected global frameworks.',
        color: '#68C3EE',
    },
    {
        h: 'Validate your programs',
        b: 'Use DQ Stars to evaluate the alignment of existing or newly created programs against the DQ framework.',
        color: '#BCD253',
    },
    {
        h: 'Add broader benchmarking',
        b: 'Connect eligible results to national or global comparative analysis where the required datasets and methodology are available.',
        color: '#EDB459',
    },
    {
        h: 'Assess the wider school ecosystem',
        b: 'Extend readiness measurement to parents, educators and school leaders as the relevant assessments become available.',
        color: '#ED6628',
    },
    {
        h: 'Localize your deployment',
        b: 'Adapt programs, assessments and communications for local languages, cultural contexts and national education requirements.',
        color: '#B9316D',
    },
];

const PbdqSchoolCosReadinessPageContent = () => {
    const classes = useStyles();
    const shared = useSharedStyles();
    const openAgentChat = useAgentChat();

    const [step, setStep] = useState(0);

    const curStep = STEPS_DATA[step];
    const selColor = STEP_COLORS[step];
    const selTint = STEP_TINTS[step];
    const selInk = STEP_INKS[step];

    const prevDisabled = step === 0;
    const nextDisabled = step === STEPS_DATA.length - 1;
    const nextLabel = step < STEPS_DATA.length - 1
        ? `Next: ${STEPS_DATA[step + 1].eyebrow.split('· ')[1]} →`
        : 'Last step';

    return (
        <div className={shared.page} style={{ background: '#FFFFFF', color: '#1B2430' }}>
            <PbdqNav />

            {/* ── SECTION 0: HERO ────────────────────────────────────────── */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroContainer}>
                    <div className={classes.heroTextCol}>
                        <h1 className={classes.heroTitle}>
                            Build students' capacity to <span style={{ color: '#ED6628' }}>thrive safely online.</span>
                        </h1>
                        <p className={classes.heroLead}>
                            Understand students’ competency and cyber-risk exposure. Turn the gaps identified into targeted learning, then measure what improves—across students, classes, schools and entire networks.
                        </p>
                        <p className={classes.heroSubLead}>
                            Aligned with your curriculum and grounded in globally recognized child online safety and digital-citizenship frameworks.
                        </p>
                        <button
                            type="button"
                            onClick={openAgentChat}
                            className={classes.heroBtn}
                        >
                            Build your solution
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    <div className={classes.heroArtCol}>
                        <img
                            src={heroImg}
                            alt="School Child Online Safety Readiness overview"
                            className={classes.heroPhoto}
                        />
                    </div>
                </div>
            </section>

            {/* ── SECTION 1: HOW IT WORKS (STEPPER) ─────────────────────── */}
            <section id="loop" aria-label="How it works" className={classes.stepperSection}>
                <div className={classes.stepperContainer}>
                    <span className={classes.sectionEyebrow}>How it works</span>
                    <div className={classes.stepperHeaderWrap}>
                        <h2 className={classes.stepperTitle}>
                            <span style={{ display: 'block' }}>Move from one-off lessons to</span>
                            <span style={{ display: 'block' }}>
                                <span style={{ color: '#ED6628' }}>measurable, continuous</span> improvement.
                            </span>
                        </h2>
                        <p className={classes.stepperIntro}>
                            It helps schools understand what students need, deliver learning targeted to those needs, and measure what changes. The cycle can be repeated as risks, priorities and student needs evolve.
                        </p>
                    </div>

                    {/* Step Tabs */}
                    <div role="tablist" aria-label="Steps" className={classes.stepsNavRow}>
                        {STEPS_DATA.map((s, idx) => {
                            const isSelected = idx === step;
                            const color = STEP_COLORS[idx];
                            const ink = STEP_INKS[idx];
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    role="tab"
                                    aria-selected={isSelected}
                                    onClick={() => setStep(idx)}
                                    className={classes.stepNavBtn}
                                >
                                    {idx < 2 && <span aria-hidden="true" className={classes.stepNavConnectingLine} />}
                                    <span
                                        className={classes.stepNavCircle}
                                        style={{
                                            background: isSelected ? color : '#FFFFFF',
                                            color: isSelected ? '#FFFFFF' : '#1B2430',
                                            border: `3px solid ${color}`,
                                        }}
                                    >
                                        {`0${idx + 1}`}
                                    </span>
                                    <span
                                        className={classes.stepNavVerb}
                                        style={{ color: ink }}
                                    >
                                        {s.eyebrow.split('· ')[1]}
                                    </span>
                                    <span className={classes.stepNavHeading}>
                                        {s.h}
                                    </span>
                                    {isSelected && (
                                        <span
                                            aria-hidden="true"
                                            className={classes.stepActiveBar}
                                            style={{ background: color }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Step Content Panel */}
                    <div
                        role="tabpanel"
                        className={classes.stepPanel}
                        style={{ borderTop: `5px solid ${selColor}` }}
                    >
                        <div className={classes.stepTextCol}>
                            <span
                                className={classes.stepPill}
                                style={{ background: selTint, color: selInk }}
                            >
                                {`Step 0${step + 1} · ${curStep.eyebrow.split('· ')[1]}`}
                            </span>
                            <h3 className={classes.stepPanelTitle}>{curStep.h}</h3>
                            <p className={classes.stepPanelPara}>{curStep.b}</p>
                            {curStep.b2 ? <p className={classes.stepPanelPara}>{curStep.b2}</p> : null}

                            <div className={classes.stepListBox}>
                                <span className={classes.stepListLabel}>{curStep.listLabel}</span>
                                <ul className={classes.stepList}>
                                    {curStep.receive.map((r, rIdx) => (
                                        <li key={rIdx} className={classes.stepListItem}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                                <path d="m5 12 5 5L20 7" />
                                            </svg>
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className={classes.stepNavButtonsRow}>
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    disabled={prevDisabled}
                                    className={classes.stepBtnPrev}
                                    style={{ opacity: prevDisabled ? 0.45 : 1 }}
                                >
                                    ← Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.min(STEPS_DATA.length - 1, s + 1))}
                                    disabled={nextDisabled}
                                    className={classes.stepBtnNext}
                                    style={{ opacity: nextDisabled ? 0.45 : 1 }}
                                >
                                    {nextLabel}
                                </button>
                            </div>
                        </div>

                        <div className={classes.stepVisualCol} style={{ background: selTint }}>
                            {step === 0 && (
                                <img
                                    loading="lazy"
                                    src={step1MeasureImg}
                                    alt={curStep.ph}
                                    className={classes.stepCardImage}
                                />
                            )}

                            {step === 1 && (
                                <PbdqVideoLightboxWrapper
                                    src={step2Video}
                                    caption="02 · Build: LearnCreator demo — building a program"
                                    style={{ width: '100%', display: 'block' }}
                                >
                                    <div className={classes.laptopWrap}>
                                        <div className={classes.laptopScreen}>
                                            <span aria-hidden="true" className={classes.laptopScreenCamera} />
                                            <video
                                                src={step2Video}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                aria-label="LearnCreator demo: building a program"
                                                className={classes.laptopVideo}
                                            />
                                        </div>
                                        <div aria-hidden="true" className={classes.laptopBase}>
                                            <span className={classes.laptopBaseNotch} />
                                        </div>
                                    </div>
                                </PbdqVideoLightboxWrapper>
                            )}

                            {step === 2 && (
                                <div className={classes.laptopWrap}>
                                    <div className={classes.laptopScreen}>
                                        <span aria-hidden="true" className={classes.laptopScreenCamera} />
                                        <img
                                            src={step3TrackImg}
                                            alt="School and Individual dashboards showing critical thinking skills improving before and after"
                                            className={classes.laptopImg}
                                        />
                                    </div>
                                    <div aria-hidden="true" className={classes.laptopBase}>
                                        <span className={classes.laptopBaseNotch} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SECTION 2: CONTINUOUS IMPROVEMENT CYCLE ──────────────── */}
            <section aria-label="The continuous-improvement cycle" className={classes.cycleSection}>
                <div className={classes.cycleContainer}>
                    <div className={classes.cycleCard}>
                        <span className={classes.cyclePill}>The continuous-improvement cycle</span>
                        <div style={{ display: 'grid', gap: 14 }}>
                            <h2 className={classes.cycleTitle}>Continuous learning, with impact you can monitor.</h2>
                            <p className={classes.cyclePara}>
                                Connect competency and risk assessments, targeted learning, and impact monitoring in one continuous cycle. Identify where students need support, address their learning gaps, and track progress over time—so each round of learning informs the next.
                            </p>
                        </div>

                        {/* Cycle Chips Row */}
                        <div className={classes.cycleStepsRow}>
                            {CYCLE_STEPS.map((c, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span
                                        className={classes.cycleChip}
                                        style={{
                                            background: c.isAccent ? '#ED6628' : 'rgba(255, 255, 255, 0.1)',
                                            color: '#FFFFFF',
                                        }}
                                    >
                                        <span className={classes.cycleChipNum}>{c.n}</span>
                                        {c.h}
                                    </span>
                                    {i < CYCLE_STEPS.length - 1 && (
                                        <span aria-hidden="true" className={classes.cycleArrow}>→</span>
                                    )}
                                </div>
                            ))}
                            <span className={classes.cycleRepeatsText}>The cycle repeats</span>
                        </div>

                        <p className={classes.cycleDashLead}>
                            Dashboards help teachers, school leaders, and network administrators turn assessment and learning data into practical next steps:
                        </p>

                        <div className={classes.cycleDashWrap}>
                            <div className={classes.cycleDashFlex}>
                                <div className={classes.cycleDashTextCol}>
                                    <ul className={classes.cycleDashList}>
                                        {DASH_POINTS.map((d, dIdx) => (
                                            <li key={dIdx} className={classes.cycleDashItem}>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 3 }}>
                                                    <path d="m5 12 5 5L20 7" />
                                                </svg>
                                                <span>
                                                    <strong style={{ color: '#FFFFFF' }}>{d.h}</strong>{' '}
                                                    <span style={{ color: '#D9E3EE' }}>{d.b}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className={classes.cycleLevelsText}>
                                        <strong style={{ color: '#FFFFFF' }}>Monitor across every level:</strong> Student · Class · Grade · School · School network
                                    </p>
                                </div>

                                <div className={classes.cycleDashVisualCol}>
                                    <div style={{ display: 'grid', filter: 'drop-shadow(0 28px 50px rgba(23, 41, 57, 0.22))' }}>
                                        <div className={classes.laptopScreenBig}>
                                            <span aria-hidden="true" className={classes.laptopScreenCameraBig} />
                                            <img
                                                src={dashboardOverviewImg}
                                                alt="School Executive Overview dashboard"
                                                className={classes.dashboardImg}
                                            />
                                        </div>
                                        <div aria-hidden="true" className={classes.laptopBaseBig}>
                                            <span className={classes.laptopBaseNotchBig} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SECTION 3: WHAT SCHOOLS RECEIVE ──────────────────────── */}
            <section aria-label="What schools receive" className={classes.receiveSection}>
                <div className={classes.receiveContainer}>
                    <span className={classes.sectionEyebrow}>What schools receive</span>
                    <h2 className={classes.sectionTitleNowrap}>Everything required to measure, deliver and improve.</h2>

                    <div className={classes.receiveGrid}>
                        {RECEIVE_LIST.map((r, idx) => (
                            <figure key={idx} className={classes.receiveCard}>
                                <figcaption className={classes.receiveCaption}>{r.t}</figcaption>
                                <PbdqImageLightboxWrapper
                                    src={r.img}
                                    alt={r.t}
                                    caption={r.t}
                                    style={{ display: 'block', width: '100%' }}
                                >
                                    <img
                                        loading="lazy"
                                        src={r.img}
                                        alt={r.t}
                                        className={classes.receiveImg}
                                    />
                                </PbdqImageLightboxWrapper>
                            </figure>
                        ))}
                    </div>

                    <p className={classes.receiveFootnote}>
                        There are no separate charges for individual school dashboards, scorecards, ordinary teacher or creator accounts, standard program delivery or the core platform features included in the deployment.
                    </p>
                </div>
            </section>

            {/* ── SECTION 4: TWO WAYS TO CREATE THE LEARNING ────────────── */}
            <section aria-label="Two ways to create the learning" className={classes.waysSection}>
                <div className={classes.waysContainer}>
                    <span className={classes.sectionEyebrow}>Two ways to create the learning</span>
                    <div style={{ display: 'grid', gap: 16 }}>
                        <h2 className={classes.sectionTitleNowrap}>Choose how your learning programs are created.</h2>
                        <p className={classes.waysSubtitle}>
                            Both models use assessment evidence to guide learning. The difference is who creates and manages the programs.
                        </p>
                    </div>

                    <div className={classes.waysGrid}>
                        {/* Way 1: Teacher-created */}
                        <div className={classes.wayCardTeacher}>
                            <div className={classes.wayImgWrap}>
                                <img
                                    src={deliveryTeacherImg}
                                    alt="Teacher creating a program in LearnCreator"
                                    className={classes.wayImg}
                                />
                            </div>
                            <div className={classes.wayBody}>
                                <span className={classes.wayPillTeacher}>Teacher-created · Recommended</span>
                                <h3 className={classes.wayOverline}>School-created programs</h3>
                                <h3 className={classes.wayHeading}>Empower educators to create learning for their own students.</h3>
                                <p className={classes.wayDesc}>
                                    Give educators access to LearnCreator so they can create and adapt programs around their curriculum, subjects and classroom needs.
                                </p>
                                <ul className={classes.wayList}>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Educators create and adapt their own programs</span>
                                    </li>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Learning fits existing subjects and timetables</span>
                                    </li>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Programs respond directly to local student needs</span>
                                    </li>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Greater educator ownership supports long-term adoption</span>
                                    </li>
                                </ul>
                                <p className={classes.wayBestForTeacher}>
                                    <strong style={{ color: '#6B7280', fontWeight: 600 }}>Best for:</strong> Schools and networks building sustainable, educator-led capability - that want online safety and digital citizenship embedded into everyday teaching.
                                </p>
                            </div>
                        </div>

                        {/* Way 2: Provider-created */}
                        <div className={classes.wayCardProvider}>
                            <div className={classes.wayImgWrap}>
                                <img
                                    src={deliveryCentralImg}
                                    alt="Centrally created program distributed to schools"
                                    className={classes.wayImg}
                                />
                            </div>
                            <div className={classes.wayBody}>
                                <span className={classes.wayPillProvider}>Provider-created</span>
                                <h3 className={classes.wayOverline}>Centrally created programs</h3>
                                <h3 className={classes.wayHeading}>Maintain consistency across every participating school.</h3>
                                <p className={classes.wayDesc}>
                                    Programs are developed centrally with your delivery provider and distributed to the schools, year groups or learner populations you select.
                                </p>
                                <ul className={classes.wayList}>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A4057" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Programs created and managed centrally</span>
                                    </li>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A4057" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Consistent learning across participating schools</span>
                                    </li>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A4057" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Tailored to assessment results and rollout priorities</span>
                                    </li>
                                    <li className={classes.wayItem}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A4057" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2 }}>
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>Central visibility of delivery and progress</span>
                                    </li>
                                </ul>
                                <p className={classes.wayBestForProvider}>
                                    <strong style={{ color: '#6B7280', fontWeight: 600 }}>Best for:</strong> School networks, education authorities, and regional or national initiatives that need consistent content, centralized curriculum control, and coordinated delivery.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SECTION 5: WHY SCHOOLS CHOOSE IT ─────────────────────── */}
            <section aria-label="Why schools choose it" className={classes.whySection}>
                <div className={classes.whyContainer}>
                    <span className={classes.sectionEyebrow}>Why schools choose it</span>
                    <h2 className={classes.sectionTitleNowrap}>Why this instead of a one-off safety assembly?</h2>

                    <div className={classes.whyGrid}>
                        {WHY_ITEMS.map((w, idx) => (
                            <div
                                key={idx}
                                className={classes.whyCard}
                                style={{
                                    background: w.tint,
                                    border: `1px solid ${w.line}`,
                                }}
                            >
                                <div className={classes.whyHeader}>
                                    <span className={classes.whyIconBox} style={{ color: w.ink }}>
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d={w.icon} />
                                        </svg>
                                    </span>
                                    <h3 className={classes.whyTitle}>{w.h}</h3>
                                </div>
                                <p className={classes.whyDesc}>{w.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SECTION 6: OPTIONAL EXTENSIONS ───────────────────────── */}
            <section aria-label="Optional extensions" className={classes.extSection}>
                <div className={classes.extContainer}>
                    <span className={classes.sectionEyebrow}>Optional extensions</span>
                    <h2 className={classes.sectionTitleNowrap}>
                        Extend the solution as your <span style={{ color: '#ED6628' }}>initiative grows.</span>
                    </h2>

                    <div className={classes.extGrid}>
                        {EXTENSIONS.map((e, idx) => (
                            <div
                                key={idx}
                                className={classes.extCard}
                                style={{ borderTop: `4px solid ${e.color}` }}
                            >
                                <span className={classes.extPlus} style={{ color: e.color }}>+</span>
                                <h3 className={classes.extTitle}>{e.h}</h3>
                                <p className={classes.extDesc}>{e.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SECTION 7: STANDARDS & FRAMEWORK ALIGNMENT ───────────── */}
            <section aria-label="Standards and framework alignment" className={classes.standardsSection}>
                <div className={classes.standardsContainer}>
                    <div className={classes.standardsTextCol}>
                        <span className={classes.sectionEyebrow}>Standards and framework alignment</span>
                        <h2 className={classes.standardsHeading}>
                            Built on a globally <span style={{ color: '#ED6628' }}>aligned foundation.</span>
                        </h2>
                        <p className={classes.standardsPara}>
                            School Child Online Safety Readiness is grounded in the DQ framework underlying IEEE 3527.1™ and can be mapped to your national curriculum and selected international frameworks.
                        </p>
                        <p className={classes.standardsPara}>
                            This gives schools a common language for defining, measuring and building the digital competencies students need to participate safely, responsibly and effectively in digital life.
                        </p>
                    </div>

                    <div className={classes.standardsCard}>
                        <span className={classes.standardsCardLabel}>Aligned with</span>
                        <span className={classes.standardsCardBig}>IEEE 3527.1™</span>
                        <p className={classes.standardsCardDesc}>
                            Mapped to your national curricula and preferred child online safety and digital citizenship frameworks.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── SECTION 8: NEXT STEP / BOTTOM CTA ────────────────────── */}
            <section aria-label="Next step" className={classes.ctaSection}>
                <div className={classes.ctaContainer}>
                    <div className={classes.ctaTextCol}>
                        <h2 className={classes.ctaTitle}>
                            <span style={{ display: 'block' }}>Measure what students need.</span>
                            <span style={{ display: 'block' }}>Teach to the gaps.</span>
                            <span style={{ display: 'block', color: '#ED6628' }}>Show what improves.</span>
                        </h2>
                        <p className={classes.ctaPara}>
                            Build a continuous, evidence-led approach to child online safety and digital citizenship across your school or network.
                        </p>
                    </div>
                    <Link
                        to={pbdqRoutes.getStarted}
                        className={classes.ctaBtn}
                    >
                        Configure School COS Readiness
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </Link>
                </div>
            </section>

            <PbdqFooter />
        </div>
    );
};

export default PbdqSchoolCosReadinessPageContent;
